import { Media } from "@blocks/components";
import { __ } from "@wordpress/i18n";
import React from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	AlignLeft,
	AlignMiddle,
	AlignRight,
	ButtonFilled,
	ButtonLink,
	ButtonOutline,
	ButtonPlain,
	TextAlignCenter,
	TextAlignLeft,
	TextAlignRight,
} from "../../../components/icons/Icons";
import {
	Background,
	Border,
	ColorPicker,
	Dimensions,
	IconPicker,
	PopoverDrawer,
	Select,
	Slider,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";
// import { isCSSVariable } from "../../../utils";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			className,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
			blockMargin,
			blockPadding,
			blockZIndex,
			cssID,
			icon,
			style,
			size,
			buttonPadding,
			image,
			modalTrigger,
			buttonIcon,
			tButtonIconPosition,
			tButtonIconSize,
			tButtonIconGap,
			closeIconPosition,
			closeIcon,
			alignment,
			width,
			height,
			maxHeight,
			modalBackground,
			padding,
			border,
			closeIconSize,
			color,
			triggerTextColor,
			triggerTextTypography,
			iconColor,
			popupBackground,
			triggerButtonBackground,
			triggerBtnTextColor,
			triggerBtnTextTypo,
			buttonIconSize,
			buttonImageSize,
		},
		setAttributes,
	} = props;
	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("Trigger", "blockart")} initialOpen>
					<ToggleButtonGroup
						value={alignment}
						onChange={(v) => {
							setAttributes({ alignment: v });
						}}
						label={__("Alignment", "blockart")}
						responsive
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
									"&[data-active]": {
										svg: {
											fill: "#fff !important",
										},
									},
								},
							},
						}}
					>
						<ToggleButton value="left">
							<AlignLeft />
						</ToggleButton>
						<ToggleButton value="center">
							<AlignMiddle />
						</ToggleButton>
						<ToggleButton value="right">
							<AlignRight />
						</ToggleButton>
					</ToggleButtonGroup>
					<ToggleButtonGroup
						value={modalTrigger}
						label="Type"
						onChange={(val) => setAttributes({ modalTrigger: val })}
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									color: "#1A202C",
									svg: {
										w: "40px !important",
										h: 6,
									},
								},
								"&[data-active]": {
									color: "#fff",
									svg: {
										fill: "#fff !important",
									},
								},
							},
						}}
					>
						<ToggleButton value="button">
							{__("Button", "magazine-blocks")}
						</ToggleButton>
						<ToggleButton value="icon">
							{__("Icon", "magazine-blocks")}
						</ToggleButton>
						<ToggleButton value="image">
							{__("Image", "magazine-blocks")}
						</ToggleButton>
						<ToggleButton value="text">
							{__("Text", "magazine-blocks")}
						</ToggleButton>
					</ToggleButtonGroup>

					{modalTrigger === "button" && (
						<>
							<ToggleButtonGroup
								value={style}
								onChange={(v) => {
									setAttributes({ style: v });
								}}
								label={__("Button Type", "magazine-blocks")}
								groupProps={{
									sx: {
										".chakra-button": {
											minH: "32px",
											svg: {
												w: "40px !important",
												h: "6",
											},
											"&[data-active]": {
												svg: {
													fill: "#fff !important",
												},
											},
										},
									},
								}}
							>
								<ToggleButton value="filled">
									<ButtonFilled />
								</ToggleButton>
								<ToggleButton value="outline">
									<ButtonOutline />
								</ToggleButton>
								<ToggleButton value="link">
									<ButtonLink />
								</ToggleButton>
								<ToggleButton value="plain">
									<ButtonPlain />
								</ToggleButton>
							</ToggleButtonGroup>
							{("filled" === style || "outline" === style) && (
								<>
									<ToggleButtonGroup
										value={size}
										onChange={(v) => {
											setAttributes({ size: v });
										}}
										label={__("Size", "magazine-blocks")}
										groupProps={{
											sx: {
												".chakra-button": {
													minH: "32px",
													color: "#1A202C",
													svg: {
														w: 6,
														h: 6,
													},
												},
												"&[data-active]": {
													color: "#fff",
												},
											},
										}}
									>
										<ToggleButton value="small">
											Small
										</ToggleButton>
										<ToggleButton value="medium">
											Medium
										</ToggleButton>
										<ToggleButton value="large">
											Large
										</ToggleButton>
										<ToggleButton value="custom">
											Custom
										</ToggleButton>
									</ToggleButtonGroup>

									{size && "custom" === size && (
										<Dimensions
											value={buttonPadding || {}}
											responsive
											label={__(
												"Padding",
												"magazine-blocks"
											)}
											defaultUnit="px"
											units={["px", "rem", "em", "%"]}
											onChange={(val) =>
												setAttributes({
													buttonPadding: val,
												})
											}
											type="padding"
											resetAttributeKey="buttonPadding"
										/>
									)}
								</>
							)}
							<IconPicker
								hasToggle={true}
								value={buttonIcon || {}}
								onChange={(val) =>
									setAttributes({ buttonIcon: val })
								}
							/>
							{buttonIcon?.enable && (
								<>
									<ToggleButtonGroup
										value={tButtonIconPosition}
										onChange={(v) => {
											setAttributes({
												tButtonIconPosition: v,
											});
										}}
										label={__(
											"Icon Position",
											"magazine-blocks"
										)}
										resetAttributeKey="tButtonIconPosition"
										groupProps={{
											sx: {
												".chakra-button": {
													minH: "32px",
													color: "#1A202C",
													svg: {
														w: 6,
														h: 6,
													},
												},
												"&[data-active]": {
													svg: {
														fill: "#fff !important",
													},
													color: "#fff",
												},
											},
										}}
									>
										<ToggleButton value="before">
											{__(
												"Before Text",
												"magazine-blocks"
											)}
										</ToggleButton>
										<ToggleButton value="after">
											{__(
												"After Text",
												"magazine-blocks"
											)}
										</ToggleButton>
									</ToggleButtonGroup>
									<Slider
										resetAttributeKey="tButtonIconSize"
										label={__(
											"Icon Size",
											"magazine-blocks"
										)}
										min={0}
										max={50}
										value={tButtonIconSize}
										onChange={(val: any) =>
											setAttributes({
												tButtonIconSize: val,
											})
										}
										responsive
										units={["px", "em", "%"]}
									/>
									<Slider
										resetAttributeKey="tButtonIconGap"
										label={__(
											"Icon Gap",
											"magazine-blocks"
										)}
										min={0}
										max={60}
										value={tButtonIconGap}
										onChange={(val: any) =>
											setAttributes({
												tButtonIconGap: val,
											})
										}
										responsive
										units={["px", "em", "%"]}
									/>
								</>
							)}
						</>
					)}
					{modalTrigger === "icon" && (
						<>
							<IconPicker
								value={icon}
								onChange={(val) => setAttributes({ icon: val })}
								hasToggle={false}
							/>
							<PopoverDrawer
								label={__("Icon Color", "magazine-blocks")}
								trigger={(props) => {
									const Trigger = () => (
										<PopoverDrawer.Trigger {...props}>
											<ZStack
												items={[
													{
														color: iconColor,
														id: "iconColor",
													},
												]}
											/>
										</PopoverDrawer.Trigger>
									);
									return <Trigger />;
								}}
							>
								<ColorPicker
									onChange={(val) =>
										setAttributes({ iconColor: val })
									}
									label={__("Color", "magazine-blocks")}
									value={iconColor}
									resetKey={"iconColor"}
								/>
							</PopoverDrawer>
							<Slider
								label={__("Icon Size", "blockart")}
								value={buttonIconSize}
								onChange={(val) =>
									setAttributes({ buttonIconSize: val })
								}
								min={0}
								max={100}
								step={1}
								forceShowUnit
								resetAttributeKey={"buttonIconSize"}
								responsive
								defaultUnit="px"
							/>
						</>
					)}
					{modalTrigger === "image" && (
						<>
							<Media
								onChange={(val: any) =>
									setAttributes({ image: val })
								}
								label={__("Image", "magazine-blocks")}
								type={"image"}
								value={image ? image : {}}
							/>
							<Slider
								label={__("Image Size", "blockart")}
								value={buttonImageSize}
								onChange={(val) =>
									setAttributes({ buttonImageSize: val })
								}
								min={0}
								max={1000}
								step={1}
								forceShowUnit
								resetAttributeKey={"buttonImageSize"}
								responsive
								defaultUnit="px"
							/>
						</>
					)}
				</Panel>
				<Panel title={__("Content Size", "magazine-blocks")}>
					<Slider
						label={__("Popup Width", "magazine-blocks")}
						value={width}
						onChange={(val) => setAttributes({ width: val })}
						min={0}
						max={100}
						step={5}
						forceShowUnit
						resetAttributeKey={"width"}
						responsive
						defaultUnit="%"
					/>
					<Slider
						onChange={(val: any) =>
							setAttributes({ maxHeight: val })
						}
						label={__("Max Height", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={0}
						max={1500}
						value={maxHeight}
						resetAttributeKey={"maxHeight"}
					/>
					<Slider
						onChange={(val: any) => setAttributes({ height: val })}
						label={__("Popup Height", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={130}
						max={1500}
						value={height}
						resetAttributeKey={"height"}
					/>
				</Panel>
				<Panel title={__("Close Button", "magazine-blocks")}>
					<IconPicker
						value={closeIcon}
						onChange={(val) => setAttributes({ closeIcon: val })}
						hasToggle={false}
					/>
					<Select
						label={__("Icon Position", "magazine-blocks")}
						value={closeIconPosition}
						onChange={(val) =>
							setAttributes({ closeIconPosition: val })
						}
						options={[
							{
								label: __("Popup Top Left", "magazine-blocks"),
								value: "popup-top-left",
							},
							{
								label: __("Popup Top Right", "magazine-blocks"),
								value: "popup-top-right",
							},
							{
								label: __("Window Top Left", "magazine-blocks"),
								value: "window-top-left",
							},
							{
								label: __(
									"Window Top Right",
									"magazine-blocks"
								),
								value: "window-top-right",
							},
						]}
					/>
					<Slider
						resetAttributeKey="closeIconSize"
						label={__("Icon Size", "magazine-blocks")}
						min={0}
						max={50}
						value={closeIconSize}
						onChange={(val: any) =>
							setAttributes({ closeIconSize: val })
						}
						responsive
						units={["px", "em", "%"]}
					/>
					<PopoverDrawer
						label={__("Icon Color", "magazine-blocks")}
						trigger={(props) => {
							const Trigger = () => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[{ color: color, id: "color" }]}
									/>
								</PopoverDrawer.Trigger>
							);
							return <Trigger />;
						}}
					>
						<ColorPicker
							onChange={(val) => setAttributes({ color: val })}
							label={__("Color", "magazine-blocks")}
							value={color}
							resetKey={"color"}
						/>
					</PopoverDrawer>
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				{modalTrigger === "button" && (
					<>
						<Panel
							title={__("Button", "magazine-blocks")}
							initialOpen
						>
							<PopoverDrawer
								label={__("Text Color", "magazine-blocks")}
								trigger={(props) => {
									const Trigger = () => (
										<PopoverDrawer.Trigger {...props}>
											<ZStack
												items={[
													{
														color: triggerBtnTextColor,
														id: "triggerBtnTextColor",
													},
												]}
											/>
										</PopoverDrawer.Trigger>
									);
									return <Trigger />;
								}}
							>
								<ColorPicker
									onChange={(val) =>
										setAttributes({
											triggerBtnTextColor: val,
										})
									}
									label={__("Color", "magazine-blocks")}
									value={triggerBtnTextColor}
									resetKey={"triggerBtnTextColor"}
								/>
							</PopoverDrawer>
							<PopoverDrawer
								label={__("Background", "magazine-blocks")}
							>
								<Reset
									saved={triggerButtonBackground}
									resetKey="triggerButtonBackground"
									onReset={(v) => {
										setAttributes({
											triggerButtonBackground: v,
										});
									}}
									buttonProps={{
										position: "absolute",
										top: "12px",
										right: "10px",
									}}
								/>
								<Background
									value={triggerButtonBackground}
									onChange={(v) =>
										setAttributes({
											triggerButtonBackground: v,
										})
									}
								/>
							</PopoverDrawer>
							{size && "custom" === size && (
								<Dimensions
									value={buttonPadding || {}}
									responsive
									label={__("Padding", "magazine-blocks")}
									defaultUnit="px"
									units={["px", "rem", "em", "%"]}
									onChange={(val) =>
										setAttributes({ buttonPadding: val })
									}
									type="buttonPadding"
								/>
							)}
							<Typography
								resetAttributeKey="triggerBtnTextTypo"
								value={triggerBtnTextTypo}
								onChange={(val: any) =>
									setAttributes({
										triggerBtnTextTypo: val,
									})
								}
							/>
						</Panel>
					</>
				)}
				{modalTrigger === "text" && (
					<>
						<Panel title={__("Text", "magazine-blocks")}>
							<PopoverDrawer
								label={__("Text Color", "magazine-blocks")}
								trigger={(props) => {
									const Trigger = () => (
										<PopoverDrawer.Trigger {...props}>
											<ZStack
												items={[
													{
														color: triggerTextColor,
														id: "triggerTextColor",
													},
												]}
											/>
										</PopoverDrawer.Trigger>
									);
									// if (isCSSVariable(triggerTextColor)) {
									// 	return (
									// 		<Box position="relative">
									// 			<Trigger />
									// 			<Global
									// 				position="absolute"
									// 				fill="primary.500"
									// 				height="14px"
									// 				width="14px"
									// 				top="-5px"
									// 				left="-10px"
									// 				bg="white"
									// 				borderRadius="full"
									// 			/>
									// 		</Box>
									// 	);
									// }
									return <Trigger />;
								}}
							>
								<ColorPicker
									onChange={(val) =>
										setAttributes({ triggerTextColor: val })
									}
									label={__("Color", "magazine-blocks")}
									value={triggerTextColor}
									resetKey={"triggerTextColor"}
								/>
							</PopoverDrawer>
							<Typography
								resetAttributeKey="triggerTextTypography"
								value={triggerTextTypography}
								onChange={(val) =>
									setAttributes({
										triggerTextTypography: val,
									})
								}
							/>
						</Panel>
					</>
				)}
				<Panel title={__("Popup", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Overlay Background", "magazine-blocks")}
					>
						<Reset
							saved={popupBackground}
							resetKey="popupBackground"
							onReset={(v) => {
								setAttributes({ popupBackground: v });
							}}
							buttonProps={{
								position: "absolute",
								top: "12px",
								right: "10px",
							}}
						/>
						<Background
							value={popupBackground}
							onChange={(v) =>
								setAttributes({ popupBackground: v })
							}
						/>
					</PopoverDrawer>
					<Dimensions
						label={__("Padding", "magazine-blocks")}
						value={padding}
						resetAttributeKey={"padding"}
						onChange={(v) => {
							setAttributes({ padding: v });
						}}
						responsive={true}
						units={["px", "em", "%"]}
					/>
					<PopoverDrawer label={__("Background", "magazine-blocks")}>
						<Reset
							saved={modalBackground}
							resetKey="modalBackground"
							onReset={(v) => {
								setAttributes({ modalBackground: v });
							}}
							buttonProps={{
								position: "absolute",
								top: "12px",
								right: "10px",
							}}
						/>
						<Background
							value={modalBackground}
							onChange={(v) =>
								setAttributes({ modalBackground: v })
							}
						/>
					</PopoverDrawer>
					<Border
						resetAttributeKey="border"
						value={border}
						onChange={(val) => setAttributes({ border: val })}
					/>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
					blockZIndex={blockZIndex}
					cssID={cssID}
					className={className}
					// hasblockHTML={false}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
